"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useRouter } from '@/platform/navigation'
import { UnsavedChangesDialog } from "@/components/unsaved-changes-dialog"

type LeaveAction = () => void

interface UnsavedChangesContextValue {
  /** Register whether the current page/form has unsaved edits. */
  setDirty: (dirty: boolean) => void
  /** True when any registered form is dirty. */
  isDirty: boolean
  /** Navigate only after confirm if dirty. */
  requestNavigate: (href: string) => void
  /** Run an action (e.g. logout) only after confirm if dirty. */
  requestLeave: (action: LeaveAction) => void
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null)

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const [dirty, setDirtyState] = useState(false)
  const dirtyRef = useRef(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const pendingActionRef = useRef<LeaveAction | null>(null)

  const setDirty = useCallback((next: boolean) => {
    dirtyRef.current = next
    setDirtyState(next)
  }, [])

  const requestLeave = useCallback((action: LeaveAction) => {
    if (!dirtyRef.current) {
      action()
      return
    }
    pendingActionRef.current = action
    setDialogOpen(true)
  }, [])

  const requestNavigate = useCallback(
    (href: string) => {
      requestLeave(() => {
        router.push(href)
      })
    },
    [requestLeave, router],
  )

  const onStay = useCallback(() => {
    pendingActionRef.current = null
    setDialogOpen(false)
  }, [])

  const onDiscard = useCallback(() => {
    const action = pendingActionRef.current
    pendingActionRef.current = null
    dirtyRef.current = false
    setDirtyState(false)
    setDialogOpen(false)
    action?.()
  }, [])

  const value = useMemo(
    () => ({ setDirty, isDirty: dirty, requestNavigate, requestLeave }),
    [setDirty, dirty, requestNavigate, requestLeave],
  )

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        open={dialogOpen}
        onStay={onStay}
        onDiscard={onDiscard}
        message="You have unsaved changes. Leave this page and discard them?"
      />
    </UnsavedChangesContext.Provider>
  )
}

export function useUnsavedChanges() {
  const ctx = useContext(UnsavedChangesContext)
  if (!ctx) {
    throw new Error("useUnsavedChanges must be used within UnsavedChangesProvider")
  }
  return ctx
}

/** Optional safe access when provider may be absent (e.g. login). */
export function useUnsavedChangesOptional() {
  return useContext(UnsavedChangesContext)
}

/**
 * Register dirty state for the current screen.
 * Also blocks browser refresh / tab close while dirty.
 */
export function useUnsavedChangesGuard(isDirty: boolean) {
  const ctx = useUnsavedChangesOptional()

  useEffect(() => {
    if (!ctx) return
    ctx.setDirty(isDirty)
    return () => ctx.setDirty(false)
  }, [ctx, isDirty])

  useEffect(() => {
    if (!isDirty) return
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [isDirty])
}
